import React, { useState, useRef, useEffect, ChangeEvent } from 'react';
import { Message, MessageVersion, ThinkingStep } from '../types';
import MessageItem from './MessageItem';
import GreenOrb from './GreenOrb';
import DamiaReflexion from './DamiaReflexion';
import ArrowSmallUp from '../../../macadamia-core/icons/ArrowSmallUp';
import './orbAnimations.css';

interface WelcomeMessage {
    title: string;
    subtitle: string;
}

interface ChatAreaProps {
    messages: Message[];
    onSendMessage: (content: string, searchParams?: { legifrance: boolean; jurisprudence: boolean; hal: boolean }) => void;
    onMessageFeedback?: (messageId: string, isPositive: boolean | null) => void;
    onRetry?: (messageId: string) => void;
    isThinking?: boolean;
    thinkingSteps?: ThinkingStep[];
    isThinkingActive?: boolean;
    welcomeMessage?: WelcomeMessage;
}

// Interface pour stocker les versions de message
interface MessageVersions {
    [messageId: string]: MessageVersion[];
}

// Interface pour stocker les dernières versions de chaque message
interface LastVersionMap {
    [originalMessageId: string]: string; // originalMessageId -> dernière version messageId
}

const ChatArea: React.FC<ChatAreaProps> = ({
    messages,
    onSendMessage,
    onMessageFeedback,
    onRetry,
    isThinking = false,
    thinkingSteps = [],
    isThinkingActive = false,
    welcomeMessage
}) => {
    const [newMessage, setNewMessage] = useState('');
    const [legalSearchActive, setLegalSearchActive] = useState(false);
    const [jurisprudenceSearchActive, setJurisprudenceSearchActive] = useState(false);
    const [halSearchActive, setHalSearchActive] = useState(false);
    const [selectedModel, setSelectedModel] = useState('Damia 1.0');
    const [showModelSelector, setShowModelSelector] = useState(false);
    // État pour stocker les versions de message
    const [messageVersions, setMessageVersions] = useState<MessageVersions>({});
    // Nouvel état pour suivre quelle version d'un message doit être affichée
    const [lastVersionMap, setLastVersionMap] = useState<LastVersionMap>({});
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const modelSelectorRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to the bottom on new messages
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Fermer le sélecteur de modèle lorsque l'utilisateur clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modelSelectorRef.current && !modelSelectorRef.current.contains(event.target as Node)) {
                setShowModelSelector(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Groupe les messages par leur parent (pour gérer les versions régénérées)
    // et crée une carte des dernières versions
    useEffect(() => {
        try {
            const newVersions: MessageVersions = {};
            const newLastVersionMap: LastVersionMap = {};

            if (!messages || messages.length === 0) {
                if (Object.keys(messageVersions).length > 0) {
                    setMessageVersions({});
                }
                if (Object.keys(lastVersionMap).length > 0) {
                    setLastVersionMap({});
                }
                return;
            }

            // Première passe : collecter toutes les versions régénérées
            messages.forEach(message => {
                if (!message || !message.id) return;

                if (message.sender === 'assistant' && message.parentMessageId) {
                    const parentId = message.parentMessageId.toString();
                    
                    if (!newVersions[parentId]) {
                        // Trouver le message parent original
                        const parentMessage = messages.find(m => m.id === parentId);
                        if (parentMessage && parentMessage.content) {
                            newVersions[parentId] = [{
                                id: parentMessage.id,
                                content: parentMessage.content,
                                timestamp: parentMessage.timestamp,
                                searchResults: parentMessage.searchResults,
                                version: 1 // Version originale = 1
                            }];
                        } else {
                            newVersions[parentId] = [];
                        }
                    }

                    // Ajouter la version courante si elle est valide
                    if (message.content) {
                        newVersions[parentId].push({
                            id: message.id,
                            content: message.content,
                            timestamp: message.timestamp,
                            searchResults: message.searchResults,
                            version: newVersions[parentId].length + 1 // Incrémenter la version
                        });
                        
                        // Mettre à jour la carte des dernières versions
                        // Pour chaque message parent, on garde l'ID de sa dernière version régénérée
                        newLastVersionMap[parentId] = message.id;
                    }
                }
            });

            // Vérifier si les versions ont réellement changé avant de mettre à jour l'état
            const versionsChanged = JSON.stringify(newVersions) !== JSON.stringify(messageVersions);
            if (versionsChanged) {
                setMessageVersions(newVersions);
            }

            // Vérifier si la carte des dernières versions a changé
            const lastVersionMapChanged = JSON.stringify(newLastVersionMap) !== JSON.stringify(lastVersionMap);
            if (lastVersionMapChanged) {
                setLastVersionMap(newLastVersionMap);
            }
        } catch (error) {
            console.error("Erreur lors de la génération des versions de messages:", error);
            if (Object.keys(messageVersions).length > 0) {
                setMessageVersions({});
            }
            if (Object.keys(lastVersionMap).length > 0) {
                setLastVersionMap({});
            }
        }
    }, [messages]);

    // Ajuster la hauteur du textarea en fonction du contenu
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            // Réinitialiser la hauteur
            textarea.style.height = 'auto';

            // Calculer la nouvelle hauteur
            const newHeight = Math.min(textarea.scrollHeight, 350);
            textarea.style.height = `${newHeight}px`;
        }
    };

    // Mettre à jour le contenu et ajuster la hauteur
    const handleTextareaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setNewMessage(e.target.value);
        // Ajuster la hauteur après la mise à jour de la valeur
        setTimeout(adjustTextareaHeight, 0);
    };

    // Initialiser la hauteur au montage et lorsque le contenu change
    useEffect(() => {
        adjustTextareaHeight();
    }, [newMessage]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (newMessage.trim() && !isThinking) {
            // Passer les paramètres de recherche au composant parent
            onSendMessage(newMessage, {
                legifrance: legalSearchActive,
                jurisprudence: jurisprudenceSearchActive,
                hal: halSearchActive
            });

            setNewMessage('');

            // Réinitialiser la hauteur du textarea après envoi
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        }
    };

    // Gestionnaire pour CMD+Entrée ou CTRL+Entrée
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            if (newMessage.trim() && !isThinking) {
                onSendMessage(newMessage, {
                    legifrance: legalSearchActive,
                    jurisprudence: jurisprudenceSearchActive,
                    hal: halSearchActive
                });

                setNewMessage('');

                // Réinitialiser la hauteur du textarea après envoi
                if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                }
            }
        }
    };

    const toggleModelSelector = () => {
        setShowModelSelector(!showModelSelector);
    };

    const selectModel = (model: string) => {
        setSelectedModel(model);
        setShowModelSelector(false);
    };

    // Gestionnaire pour la fonction Réessayer
    const handleRetry = (messageId: string) => {
        if (onRetry) {
            onRetry(messageId);
        }
    };

    // Nouvelle fonction pour préparer les messages à afficher
    const getDisplayMessages = () => {
        if (!messages || messages.length === 0) return [];

        const result: Message[] = [];
        const processedParentIds = new Set<string>();

        for (const message of messages) {
            if (!message || !message.id) continue;

            // Si c'est un message utilisateur, l'ajouter toujours
            if (message.sender === 'user') {
                result.push(message);
                continue;
            }

            // Pour les messages de l'assistant
            if (message.sender === 'assistant') {
                // Si c'est une version régénérée
                if (message.parentMessageId) {
                    const parentId = message.parentMessageId.toString();
                    
                    // Si ce message est la dernière version de son parent
                    // ET si nous n'avons pas déjà traité ce parent
                    if (lastVersionMap[parentId] === message.id && !processedParentIds.has(parentId)) {
                        result.push(message);
                        processedParentIds.add(parentId);
                    }
                } 
                // Si c'est un message original et qu'il n'a pas de versions régénérées
                else if (!lastVersionMap[message.id]) {
                    result.push(message);
                }
                // Si c'est un message original qui a des versions régénérées mais qu'on n'a pas encore affiché sa dernière version
                else if (!processedParentIds.has(message.id)) {
                    // On cherche la dernière version de ce message dans les messages
                    const lastVersionId = lastVersionMap[message.id];
                    const lastVersion = messages.find(m => m.id === lastVersionId);
                    
                    if (lastVersion) {
                        // On ajoute la dernière version à la place du message original
                        result.push(lastVersion);
                        processedParentIds.add(message.id);
                    } else {
                        // Fallback si la dernière version n'est pas trouvée
                        result.push(message);
                        processedParentIds.add(message.id);
                    }
                }
            }
        }

        return result;
    };

    // Obtenir les messages à afficher
    const displayMessages = getDisplayMessages();

    return (
        <div className="flex flex-col h-full dark:bg-mcdm-third-500">
            {/* Zone des messages avec scroll indépendant */}
            <div className="flex-1 overflow-hidden">
                <div className="h-[calc(100vh-220px)] overflow-y-auto px-40 pt-5">
                    {displayMessages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full">
                            <GreenOrb />
                            <div className="text-center text-mcdm-third-400 dark:text-mcdm-primary-300">
                                <h3 className="text-xl font-platypi">{welcomeMessage?.title || 'Bienvenue sur Damia'}</h3>
                                <p>{welcomeMessage?.subtitle || 'Comment puis-je vous aider ?'}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {displayMessages.map((message, index) => {
                                // Ignorer les messages invalides
                                if (!message || !message.id || !message.content) {
                                    return null;
                                }

                                // Déterminer si on affiche le séparateur de date
                                const messageDate = new Date(message.timestamp);
                                const messageDateStr = messageDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                                let showDateSeparator = false;

                                if (index === 0) {
                                    showDateSeparator = true;
                                } else {
                                    const prevMessage = displayMessages[index - 1];
                                    if (prevMessage?.timestamp) {
                                        const prevDateStr = new Date(prevMessage.timestamp).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                                        showDateSeparator = messageDateStr !== prevDateStr;
                                    }
                                }

                                // Formater le label de la date
                                const today = new Date();
                                const yesterday = new Date(today);
                                yesterday.setDate(yesterday.getDate() - 1);
                                let dateLabel = messageDateStr.charAt(0).toUpperCase() + messageDateStr.slice(1);
                                if (messageDate.toDateString() === today.toDateString()) {
                                    dateLabel = "Aujourd'hui";
                                } else if (messageDate.toDateString() === yesterday.toDateString()) {
                                    dateLabel = 'Hier';
                                }

                                // Si ce message a un parent, vérifier s'il a des versions
                                let versions;
                                if (message.parentMessageId) {
                                    // Ce message est une version régénérée, récupérer toutes les versions du parent
                                    versions = messageVersions[message.parentMessageId];
                                } else {
                                    // Ce message est original, vérifier s'il a des versions
                                    versions = messageVersions[message.id];
                                }

                                return (
                                    <React.Fragment key={message.id}>
                                        {showDateSeparator && (
                                            <div className="flex items-center justify-center my-4">
                                                <span className="px-3 py-1 text-xs font-medium text-mcdm-third-300 dark:text-mcdm-primary-300 bg-mcdm-primary-300 dark:bg-mcdm-third-300 rounded-full">
                                                    {dateLabel}
                                                </span>
                                            </div>
                                        )}
                                        {message.sender === 'assistant' && message.thinkingSteps && message.thinkingSteps.length > 0 && (
                                            <DamiaReflexion
                                                steps={message.thinkingSteps.map(s => ({
                                                    conversationId: '',
                                                    stepId: s.stepId,
                                                    label: s.label,
                                                    status: s.status,
                                                    order: s.order,
                                                    data: s.data,
                                                    timestamp: 0
                                                }))}
                                                isActive={false}
                                                initialCollapsed={true}
                                            />
                                        )}
                                        <MessageItem
                                            message={message}
                                            messageVersions={versions}
                                            onRetry={() => handleRetry(message.id)}
                                            onFeedback={onMessageFeedback}
                                        />
                                    </React.Fragment>
                                );
                            })}

                            {/* Indicateur que l'IA réfléchit */}
                            {isThinking && (
                                thinkingSteps.length > 0 ? (
                                    <DamiaReflexion steps={thinkingSteps} isActive={isThinkingActive} />
                                ) : (
                                    <div className="flex w-full mb-10 justify-start">
                                        <div className="bg-mcdm-primary-200 dark:bg-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-primary-100 px-4 py-3 rounded-lg">
                                            <div className="flex items-center space-x-2">
                                                <div className="flex space-x-1">
                                                    <div className="w-2 h-2 bg-mcdm-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                                    <div className="w-2 h-2 bg-mcdm-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                                    <div className="w-2 h-2 bg-mcdm-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                                </div>
                                                <span className="text-sm">Un instant je vous prie...</span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}

                            <div ref={messagesEndRef} className="pb-4" />
                        </>
                    )}
                </div>
            </div>

            {/* Zone de saisie fixe en bas */}
            <div className="border-t border-gray-200 dark:border-gray-700">
                <div className="px-40 py-4">
                    <form onSubmit={handleSubmit}>
                        <div className="flex flex-col border border-gray-300 dark:border-gray-600 rounded-xl 
                             focus-within:ring-1 focus-within:ring-mcdm-secondary-200 overflow-hidden">
                            <textarea
                                ref={textareaRef}
                                value={newMessage}
                                onChange={handleTextareaChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Écrivez votre message..."
                                disabled={isThinking}
                                className={`w-full px-4 py-2
                                    min-h-[40px] max-h-[240px] resize-none overflow-y-auto border-none
                                    bg-transparent text-mcdm-third-500 dark:text-mcdm-primary-200 
                                    focus:outline-none focus:ring-0
                                    ${isThinking ? 'opacity-50 cursor-not-allowed' : ''}`}
                                rows={1}
                            />
                            <div className="flex justify-between items-center p-2">
                                <div className="flex space-x-2">
                                    <button
                                        type="button"
                                        onClick={() => setLegalSearchActive(!legalSearchActive)}
                                        disabled={isThinking}
                                        className={`px-3 py-1.5 text-xs rounded-full transition-colors duration-200 border
                                            ${legalSearchActive
                                                ? 'bg-mcdm-green-400 border-mcdm-green-500 text-white'
                                                : 'bg-transparent border-gray-300 dark:border-gray-600 text-mcdm-third-500 dark:text-mcdm-primary-200'
                                            }
                                            ${isThinking ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        Recherche Légifrance
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setJurisprudenceSearchActive(!jurisprudenceSearchActive)}
                                        disabled={isThinking}
                                        className={`px-3 py-1.5 text-xs rounded-full transition-colors duration-200 border
                                            ${jurisprudenceSearchActive
                                                ? 'bg-mcdm-green-400 border-mcdm-green-500 text-white'
                                                : 'bg-transparent border-gray-300 dark:border-gray-600 text-mcdm-third-500 dark:text-mcdm-primary-200'
                                            }
                                            ${isThinking ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        Recherche Jurisprudence
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setHalSearchActive(!halSearchActive)}
                                        disabled={isThinking}
                                        className={`px-3 py-1.5 text-xs rounded-full transition-colors duration-200 border
                                            ${halSearchActive
                                                ? 'bg-mcdm-green-400 border-mcdm-green-500 text-white'
                                                : 'bg-transparent border-gray-300 dark:border-gray-600 text-mcdm-third-500 dark:text-mcdm-primary-200'
                                            }
                                            ${isThinking ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        Recherche HAL
                                    </button>
                                </div>
                                <div className="flex items-center">
                                    <div className="relative mr-2" ref={modelSelectorRef}>
                                        <button
                                            type="button"
                                            onClick={toggleModelSelector}
                                            disabled={isThinking}
                                            className={`flex items-center px-3 py-1.5 text-xs rounded-full transition-colors duration-200 border
                                                bg-transparent border-gray-300 dark:border-gray-600 text-mcdm-third-500 dark:text-mcdm-primary-200
                                                hover:bg-gray-100 dark:hover:bg-gray-700
                                                ${isThinking ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            aria-label="Sélectionner un modèle d'IA"
                                        >
                                            <span className="mr-1">
                                                {selectedModel}
                                            </span>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-3 w-3"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d={showModelSelector ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                                                />
                                            </svg>
                                        </button>
                                        {showModelSelector && (
                                            <div className="absolute bottom-full mb-1 right-0 w-40 bg-white dark:bg-mcdm-third-500 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg z-10">
                                                <div className="py-1">
                                                    <button
                                                        onClick={() => selectModel('Damia 1.0')}
                                                        className="flex items-center px-4 py-2 text-xs text-left w-full hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-mcdm-primary-100"
                                                    >
                                                        <span className="mr-2">
                                                            {selectedModel === 'Damia 1.0' && (
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="h-3 w-3 text-mcdm-green-400"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth={2}
                                                                        d="M5 13l4 4L19 7"
                                                                    />
                                                                </svg>
                                                            )}
                                                        </span>
                                                        <span>Damia 1.0</span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={isThinking || !newMessage.trim()}
                                        className={`p-2 
                                            ${isThinking || !newMessage.trim()
                                                ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                                                : 'bg-mcdm-green-400 hover:bg-mcdm-green-500'
                                            } 
                                            text-white rounded-lg transition-colors duration-200`}
                                        aria-label="Envoyer"
                                    >
                                        <ArrowSmallUp className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ChatArea;